import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useScrollReveal } from '../hooks/useScrollReveal';

const SECTIONS = [
  {
    title: '1. Introduction',
    content: `CodeViora ("we", "us", or "our") operates the website codeviora.com (the "Service"). This Privacy Policy explains how we collect, use, and protect your information when you visit our website or use our services. By using our website, you agree to the practices described in this policy.`,
  },
  {
    title: '2. Information We Collect',
    content: `We collect information in the following ways:`,
    list: [
      'Contact Form Data: When you fill out our contact form, we collect your name, email address, phone number, company name, service of interest, budget range, and project details.',
      'Automatically Collected Data: When you visit our website, we automatically collect certain information through Google Analytics, including your IP address, browser type, device information, pages visited, time spent on pages, and referral source.',
      'Cookies: We may use cookies to improve your browsing experience. You can control cookie preferences through your browser settings.',
    ],
  },
  {
    title: '3. How We Use Your Information',
    content: `We use the information we collect to:`,
    list: [
      'Respond to your inquiries and provide the services you request',
      'Send you a quote or proposal based on your project requirements',
      'Improve our website and understand how visitors use our Service',
      'Communicate with you about your project, including updates and follow-ups',
      'Comply with legal obligations',
    ],
  },
  {
    title: '4. Google Analytics',
    content: `We use Google Analytics to understand how visitors interact with our website. Google Analytics collects information such as how often users visit the site, what pages they visit, and what other sites they used prior to coming to our site. We use this information only to improve our website. Google Analytics collects only the IP address assigned to you on the date you visit this site, rather than your name or other identifying information. You can learn more about Google's data practices at policies.google.com/privacy, and you can opt out of Google Analytics tracking by installing the Google Analytics Opt-out Browser Add-on.`,
  },
  {
    title: '5. Data Sharing & Third Parties',
    content: `We do not sell, trade, or rent your personal information to third parties. We may share your information with:`,
    list: [
      'Service providers who help us operate our website and business (such as email service providers)',
      'Legal authorities, if required by law or to protect our legal rights',
    ],
  },
  {
    title: '6. Data Security',
    content: `We take reasonable measures to protect the information you provide to us from loss, theft, misuse, and unauthorized access. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.`,
  },
  {
    title: '7. Data Retention',
    content: `We retain the personal information you provide through our contact form for as long as necessary to respond to your inquiry and maintain business records, unless a longer retention period is required by law.`,
  },
  {
    title: '8. Your Rights',
    content: `Depending on your location, you may have the right to:`,
    list: [
      'Request access to the personal data we hold about you',
      'Request correction of inaccurate personal data',
      'Request deletion of your personal data',
      'Withdraw consent where we rely on consent to process your data',
    ],
  },
  {
    title: '9. Children\'s Privacy',
    content: `Our Service is not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us so we can take appropriate action.`,
  },
  {
    title: '10. Changes to This Policy',
    content: `We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically.`,
  },
  {
    title: '11. Contact Us',
    content: `If you have any questions about this Privacy Policy or how we handle your personal information, please contact us at:`,
    contact: true,
  },
];

export default function PrivacyPolicy() {
  useScrollReveal();

  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Read CodeViora's privacy policy to understand how we collect, use, and protect your personal information."
        canonical="/privacy-policy"
      />

      {/* Hero */}
      <section className="bg-grid-black pt-16 pb-16 md:pt-20 md:pb-20 relative overflow-hidden border-white/10 rounded-b-[50px] md:rounded-b-[100px]">
        <div className="container-max relative">
          <div className="max-w-2xl animate-fade-up">
            <span className="section-label-inv">Legal</span>
            <h1 className="section-title-inv text-4xl md:text-5xl mt-2">PRIVACY POLICY.</h1>
            <p className="section-sub-inv">Last updated: June 29, 2026</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="bg-grid-white py-14 md:py-20">
        <div className="container-max max-w-3xl">
          <div className="flex flex-col gap-10">
            {SECTIONS.map(({ title, content, list, contact }) => (
              <div key={title} className="reveal">
                <h2 className="text-xl md:text-2xl text-black mb-3" style={{ fontFamily: 'Anton,sans-serif', fontWeight: 500 }}>
                  {title}
                </h2>
                <p className="text-sm text-black/60 leading-relaxed">{content}</p>
                {list && (
                  <ul className="mt-4 flex flex-col gap-2.5">
                    {list.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-black/60">
                        <span className="w-1.5 h-1.5 rounded-full bg-black/30 flex-shrink-0 mt-1.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
                {contact && (
                  <div className="mt-4 flex flex-col gap-2 text-sm">
                    <p className="text-black/70">
                      <strong className="text-black">Email:</strong>{' '}
                      <a href="mailto:best@codeviora.com" className="underline underline-offset-2 hover:text-black">
                        codeviora@gmail.com
                      </a>
                    </p>
                    <p className="text-black/70">
                      <strong className="text-black">Phone:</strong>{' '}
                      <a href="tel:+918699393272" className="underline underline-offset-2 hover:text-black">
                        +91 8699393272
                      </a>
                    </p>
                    <p className="text-black/70">
                      <strong className="text-black">Website:</strong>{' '}
                      <Link to="/contact" className="underline underline-offset-2 hover:text-black">
                        codeviora.com/contact
                      </Link>
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Back to home */}
          <div className="mt-14 pt-10 border-t border-black/10 reveal">
            <Link to="/" className="btn-outline text-sm">
              ← Back to Home
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
